import React, { createContext, useContext, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import successMessage from '../components/messages/successMessage';
import failedMessage from '../components/messages/failedMessage';

const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  const addNotification = useCallback((notification) => {
    const id = Date.now() + Math.random();
    const newNotification = {
      id,
      type: 'error', // default type
      title: 'Error',
      message: '',
      duration: 5000, // 5 seconds default
      ...notification,
    };

    setNotifications(prev => [...prev, newNotification]);

    // Auto-remove notification after duration
    if (newNotification.duration > 0) {
      setTimeout(() => {
        removeNotification(id);
      }, newNotification.duration);
    }

    return id;
  }, []);

  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  // Specific notification types
  const showError = useCallback((message, title = 'Error', options = {}) => {
    // Show toast using react-toastify with consistent styling
    toast.error(message, {
      ...failedMessage(message),
      position: options.position || "top-right", // Changed to top-right
    });
    
    // Also add to internal state for any components that might need it
    return addNotification({
      type: 'error',
      title,
      message,
      ...options,
    });
  }, [addNotification]);

  const showSuccess = useCallback((message, title = 'Success', options = {}) => {
    // Show toast using react-toastify with consistent styling
    toast.success(message, {
      ...successMessage(message),
      position: options.position || "top-right", // Changed to top-right
      duration: 3000,
    });
    
    // Also add to internal state for any components that might need it
    return addNotification({
      type: 'success',
      title,
      message,
      duration: 3000,
      ...options,
    });
  }, [addNotification]);

  const showWarning = useCallback((message, title = 'Warning', options = {}) => {
    // Show toast using react-toastify with consistent styling
    toast.warning(message, {
      position: options.position || "top-right", // Changed to top-right
    });
    
    // Also add to internal state for any components that might need it
    return addNotification({
      type: 'warning',
      title,
      message,
      ...options,
    });
  }, [addNotification]);

  const showInfo = useCallback((message, title = 'Information', options = {}) => {
    // Show toast using react-toastify with consistent styling
    toast.info(message, {
      position: options.position || "top-right", // Changed to top-right
    });
    
    // Also add to internal state for any components that might need it
    return addNotification({
      type: 'info',
      title,
      message,
      ...options,
    });
  }, [addNotification]);

  const value = {
    notifications,
    addNotification,
    removeNotification,
    clearAll,
    showError,
    showSuccess,
    showWarning,
    showInfo,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;